import React, { useCallback, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import translate from '../../../../commonComponents/translate';
import JourneyStepProp from '../../propTypes/journeyStepProp';
import { JOURNEY_STEP_STATUS } from '../../../../services/utilityFunctions';
import Carousel from '../../../../commonComponents/carousel/Carousel';
import ReactLoader from '../../../../commonComponents/loader/ReactLoader';
import Notifications from '../../../../services/notifications';
import ShopCartContext from '../../../../contextProviders/ShopCartContext';
import StatusLabel from '../../../../commonComponents/StatusLabel';
import LoggedUserContext from '../../../../contextProviders/LoggedUserContext';
import SkipStepButton from './components/SkipStepButton';
import ShopProductItem from '../../../shop/shopItems/ShopProductItem';
import AddPackProduct from '../../../shop/shopItems/pack/advancedPackComponents/AddPackProduct';
import { useModal } from '../../../../services/customHooks';
import { PACK_TYPE } from '../../../shop/propTypes/productProp';

function pickSubscriptionFrequency(list) {
  if (!Array.isArray(list) || list.length === 0) return undefined;
  const is = (a, b) => a.toLowerCase() === b.toLowerCase();

  return (
    list.find(v => is(v, 'monthly')) ||
    list.find(v => is(v, 'yearly')) ||
    list.find(v => !is(v, 'only_once')) ||
    list[0]
  );
}
function JourneyContentPurchase(props) {
  const { loggedUser } = useContext(LoggedUserContext);
  const { t, step, slug: journeySlug, onSkipStep } = props;
  const {
    marketProductVariations = [],
    marketProductPacks = [],
    shouldPurchaseAllProducts,
  } = step;
  const [variationList, setVariationList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const { setShopCart } = useContext(ShopCartContext);

  const { shippingCountry, currency } = loggedUser;
  const getVariationList = () => {
    setIsLoading(true);
    axios
      .get(
        `/api/market/product/available/list?country=${shippingCountry}&currency=${currency}`,
        {
          params: {
            variations: marketProductVariations,
            products: marketProductPacks,
          },
          headers: {
            'Accept-Version': '1.0.0',
            'X-Gamification-API': 'Games',
          },
        },
      )
      .then(({ data: response }) => {
        const result = response?.data.filter(o1 =>
          [...marketProductVariations, ...marketProductPacks].some(
            o2 => o1._id === o2,
          ),
        );
        setVariationList(result);
        setIsLoading(false);
      })
      .catch(err => {
        setIsLoading(false);
        Notifications.showErrorFromRequest(err, 'error_fetchProducts');
      });
  };

  useEffect(() => {
    getVariationList();
  }, []);

  const addToCart = (productId = null) => {
    const productsToBuy = productId
      ? variationList?.filter(v => v._id === productId)
      : variationList;

    const advancedPacks = productsToBuy.filter(
      product => product?.packType === PACK_TYPE.ADVANCED_PACK,
    );

    const hybridPacks = productsToBuy.filter(
      product => product?.packType === PACK_TYPE.HYBRID_PACK,
    );

    if (advancedPacks?.length > 0) {
      let error = false;
      advancedPacks.forEach(pack => {
        if (!pack?.packVariations || pack?.packVariations.length === 0) {
          Notifications.showNotificationError(
            t('error'),
            t(
              'advancedPack.error.amountVariationsPerAdvancedPack.custom',
              pack?.amountVariationsPerAdvancedPack,
              pack?.name,
            ),
          );
          error = true;
        }
      });
      if (error) return;
    }

    if (hybridPacks?.length > 0) {
      let error = false;
      hybridPacks.forEach(pack => {
        if (pack?.packType === PACK_TYPE.HYBRID_PACK) {
          const availableSections = {};
          pack?.multiPackVariationsAvailable?.forEach(variationSection => {
            const { _id: sectionId, amountVariationsToChoose } =
              variationSection;

            const quantitiesSelected =
              pack?.packVariations
                ?.filter(variation => variation.sectionId === sectionId)
                ?.reduce(
                  (acc, variation) => acc + (variation.quantity ?? 1),
                  0,
                ) ?? 0;

            availableSections[sectionId] =
              quantitiesSelected === amountVariationsToChoose;
          });

          const allSectionsValid = Object.values(availableSections).every(
            v => v,
          );

          if (!allSectionsValid) {
            Notifications.showNotificationError(
              t('error'),
              t(
                'advancedPack.error.amountVariationsPerAdvancedPack.custom',
                pack?.multiPackVariationsAvailable?.reduce(
                  (acc, variation) => acc + variation.amountVariationsToChoose,
                  0,
                ),
                pack?.name,
              ),
            );
            error = true;
          }
        }
      });
      if (error) return;
    }

    setShopCart(
      productsToBuy?.map(v => {
        let price =
          v.userPrice && v.userPrice?.total >= 0
            ? v.userPrice.total / 100
            : v.userPrice / 100;
        if (v.userPromotionPrice) {
          price =
            v.userPromotionPrice && v.userPromotionPrice?.total >= 0
              ? v.userPromotionPrice.total / 100
              : v.promotionPrice / 100;
        }

        let cartPackVariations =
          v?.packType === PACK_TYPE.ADVANCED_PACK
            ? v?.packVariations?.map(variation => ({
                variation: variation?._id,
                quantity: variation?.quantity,
              }))
            : undefined;

        if (v?.packType === PACK_TYPE.HYBRID_PACK) {
          const allVariations = [
            ...(v?.parentProduct?.packVariations?.map(p => ({
              quantity: p?.quantity,
              variation: p?.variation?._id,
              _id: p?.variation?._id,
            })) || []),
            ...(v?.packVariations?.map(variation => ({
              quantity: variation.quantity,
              variation: variation._id,
              _id: variation._id,
            })) || []),
          ];

          const variationMap = allVariations.reduce((acc, item) => {
            const key = item.variation || item._id;
            if (acc[key]) {
              acc[key].quantity += item.quantity;
            } else {
              acc[key] = { ...item };
            }
            return acc;
          }, {});

          cartPackVariations = Object.values(variationMap);
        }

        return {
          isPack: v?.parentProduct?.isPack,
          id: v._id,
          amount: 1,
          va: v.va,
          commissionable: v.commissionable,
          price,
          isAdvancedPack: v?.packType === PACK_TYPE.ADVANCED_PACK,
          isHybridPack: v?.packType === PACK_TYPE.HYBRID_PACK,
          amountVariationsPerAdvancedPack: v?.amountVariationsPerAdvancedPack,
          packVariations: cartPackVariations,
          subscriptionType: v?.parentProduct?.availableForSubscription
            ? pickSubscriptionFrequency(
                v.parentProduct?.availableSubscriptionFrequency,
              )
            : false,
          availableForSubscription: v?.parentProduct?.availableForSubscription,
          availableSubscriptionFrequency:
            v?.parentProduct?.availableSubscriptionFrequency,
        };
      }),
    );
    window.location.href = `/shop/checkout?journey=${journeySlug}`;
  };

  /** region Advanced Pack Modal */
  const [advancedPackModal, setAdvancedPackModal] = useModal({
    show: false,
    data: null,
  });
  const handleOpenModal = useCallback(
    product => {
      const productItem =
        product?.packType === PACK_TYPE.HYBRID_PACK ||
        product?.packType === PACK_TYPE.ADVANCED_PACK
          ? {
              ...product,
              packVariations:
                variationList.find(p => p._id === product._id)
                  ?.packVariations || [],
            }
          : product;
      setAdvancedPackModal({ show: true, data: productItem });
    },
    [setAdvancedPackModal, variationList],
  );
  const handleCloseModal = useCallback(
    packVariations => {
      setAdvancedPackModal({ show: false, data: null });
      // used only because state is not immediately updated
      // when packVariationsAvailable is empty and packType is HYBRID_PACK we get only fixed products
      const isValid =
        advancedPackModal?.data?.packVariationsAvailable?.length === 0 &&
        advancedPackModal?.data?.packType === PACK_TYPE.HYBRID_PACK
          ? true
          : packVariations?.length > 0;

      if (isValid && !shouldPurchaseAllProducts) {
        const product = advancedPackModal?.data ?? {};
        const {
          _id,
          userPrice = {},
          userPromotionPrice,
          promotionPrice,
          isPack,
          va,
          commissionable,
          packType,
          amountVariationsPerAdvancedPack,
        } = product;
        const isAdvancedPack = packType === PACK_TYPE.ADVANCED_PACK;
        const isHybridPack = packType === PACK_TYPE.HYBRID_PACK;

        // Calculate the price
        let price =
          userPrice?.total >= 0 ? userPrice.total / 100 : userPrice / 100;

        if (userPromotionPrice) {
          price =
            userPromotionPrice?.total >= 0
              ? userPromotionPrice.total / 100
              : promotionPrice / 100;
        }

        let cartPackVariations =
          packType === PACK_TYPE.ADVANCED_PACK
            ? packVariations?.map(variation => ({
                variation: variation?._id,
                quantity: variation?.quantity,
              }))
            : undefined;

        if (isHybridPack) {
          const allVariations = [
            ...(product?.parentProduct?.packVariations?.map(p => ({
              quantity: p?.quantity,
              variation: p?.variation?._id,
              _id: p?.variation?._id,
            })) || []),
            ...(packVariations?.map(v => ({
              quantity: v.quantity,
              variation: v._id,
              _id: v._id,
            })) || []),
          ];

          cartPackVariations = allVariations.reduce((acc, item) => {
            const key = item.variation || item._id;
            const existing = acc.find(
              variation => (variation.variation || item._id) === key,
            );

            if (existing) {
              existing.quantity += item.quantity;
            } else {
              acc.push({ ...item });
            }

            return acc;
          }, []);
        }

        const productForCart = {
          isPack,
          id: _id,
          amount: 1,
          va,
          commissionable,
          price,
          isAdvancedPack,
          isHybridPack,
          amountVariationsPerAdvancedPack,
          packVariations: cartPackVariations,
          subscriptionType: product?.availableForSubscription
            ? pickSubscriptionFrequency(product?.availableSubscriptionFrequency)
            : false,
          availableForSubscription: product?.availableForSubscription,
          availableSubscriptionFrequency:
            product?.availableSubscriptionFrequency,
        };
        setShopCart([productForCart]);

        window.location.href = `/shop/checkout?journey=${journeySlug}`;
      }

      if (isValid && shouldPurchaseAllProducts) {
        setVariationList(prevProductsList =>
          prevProductsList?.map(product => {
            if (
              product?._id === advancedPackModal?.data?._id &&
              (product?.packType === PACK_TYPE.ADVANCED_PACK ||
                product?.packType === PACK_TYPE.HYBRID_PACK)
            ) {
              let cartPackVariations =
                product?.packType === PACK_TYPE.ADVANCED_PACK
                  ? packVariations
                  : undefined;

              if (product?.packType === PACK_TYPE.HYBRID_PACK) {
                cartPackVariations = [
                  ...(packVariations?.map(v => ({
                    ...v,
                    quantity: v.quantity,
                    variation: v._id,
                    _id: v._id,
                  })) || []),
                ];
              }
              return {
                ...product,
                packVariations: cartPackVariations,
              };
            }

            return product;
          }),
        );
      }
    },
    [setAdvancedPackModal],
  );
  /** endregion Advanced Pack Modal */

  return (
    <>
      {advancedPackModal?.show && (
        <AddPackProduct
          product={{
            ...advancedPackModal?.data,
            pack: {
              ...advancedPackModal?.data?.pack,
              packType: advancedPackModal?.data?.packType,
              amountVariationsPerAdvancedPack:
                advancedPackModal?.data?.amountVariationsPerAdvancedPack,
            },
          }}
          t={t}
          user={loggedUser}
          onClose={handleCloseModal}
          selectedVariations={advancedPackModal?.data?.packVariations || []}
          isAvailableEmpty={
            advancedPackModal?.data?.multiPackVariationsAvailable?.length ===
              0 && advancedPackModal?.data?.packType === PACK_TYPE.HYBRID_PACK
          }
          multiPackVariationsAvailable={
            advancedPackModal?.data?.multiPackVariationsAvailable
          }
          isBySection={
            advancedPackModal?.data?.multiPackVariationsAvailable?.length > 0
          }
        />
      )}
      <div className="m-portlet m-portlet-rounded">
        <div className="m-portlet__body purchase-step">
          <ReactLoader isLoading={isLoading}>
            <div className="purchase-header row">
              <div className="col-lg-9 col-6 h6-container">
                <h6>{t('journey.stepType.purchase.items')}</h6>
                <span>{`(${t(
                  shouldPurchaseAllProducts
                    ? 'journey.stepType.purchase.items.subtitle'
                    : 'journey.stepType.purchase.item.subtitle',
                )})`}</span>
              </div>
              <div className="col-lg-3 col-6 btn-container">
                {onSkipStep && (
                  <div className="skip-wrapper">
                    <SkipStepButton onClick={onSkipStep} />
                  </div>
                )}
                {shouldPurchaseAllProducts ? (
                  <>
                    {step.status !== JOURNEY_STEP_STATUS.DONE ? (
                      <button
                        type="button"
                        onClick={() => addToCart(null)}
                        className="addAllToCart addAllToCart-added"
                      >
                        <i className="la la-shopping-cart" />
                        {t('shop.goToCheckout')}
                      </button>
                    ) : (
                      <div>
                        <StatusLabel t={t} status="done" />
                      </div>
                    )}
                  </>
                ) : null}
              </div>
            </div>
            <div className="step-purchase-wrapper">
              <Carousel>
                {variationList
                  ?.sort((a, b) => a?.name?.localeCompare(b.name))
                  ?.map(item => {
                    const { parentProduct } = item;
                    const productItem =
                      parentProduct?.pack?.packType === PACK_TYPE.HYBRID_PACK ||
                      parentProduct?.pack?.packType === PACK_TYPE.ADVANCED_PACK
                        ? {
                            ...parentProduct,
                            packVariations:
                              variationList.find(
                                p => p._id === parentProduct._id,
                              )?.packVariations || [],
                          }
                        : parentProduct;
                    const product = parentProduct?.isPack
                      ? {
                          ...item,
                          ...productItem,
                          pack: {
                            ...item.pack,
                            userPrice: item.userPrice,
                            userPromotionPrice: item.userPromotionsPrice,
                          },
                        }
                      : {
                          ...parentProduct,
                          variations: [item],
                        };
                    return (
                      <ShopProductItem
                        product={product}
                        t={t}
                        onBuyNow={
                          !shouldPurchaseAllProducts
                            ? () => addToCart(item._id)
                            : null
                        }
                        onOpenAdvancedPackModal={handleOpenModal}
                        journeySlug={journeySlug}
                      />
                    );
                  })}
              </Carousel>
            </div>
          </ReactLoader>
        </div>
      </div>
    </>
  );
}

JourneyContentPurchase.propTypes = {
  t: PropTypes.func.isRequired,
  step: PropTypes.shape(JourneyStepProp.propType).isRequired,
  slug: PropTypes.string.isRequired,
  onSkipStep: PropTypes.func,
};

JourneyContentPurchase.defaultProps = {
  onSkipStep: null,
};

export default translate(JourneyContentPurchase);
